(() => {
  'use strict';

  const VERSION = '1.5.1';
  if (window.MoztechHeroExperience?.version === VERSION) return;

  class MoztechHeroExperienceInstance {
    constructor(root) {
      this.root = root;
      this.video = root.querySelector('[data-moztech-hero-video]');
      this.sources = this.video ? [...this.video.querySelectorAll('source[data-src]')] : [];
      this.featureButtons = [...root.querySelectorAll('[data-moztech-feature-trigger]')];
      this.mobileQuery = window.matchMedia('(max-width: 768px)');
      this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.isDesignMode = Boolean(
        window.Shopify?.designMode
        || document.documentElement.classList.contains('shopify-design-mode')
      );
      this.isBurnHero = root.dataset.burnEnabled === 'true';
      const userAgent = navigator.userAgent || '';
      const isIOSWebKit = /iPad|iPhone|iPod/.test(userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isDesktopSafari = /Safari/.test(userAgent)
        && !/Chrome|Chromium|CriOS|Edg|OPR|Android/.test(userAgent);
      const platform = navigator.userAgentData?.platform || navigator.platform || '';
      this.isApplePlatform = isIOSWebKit
        || isDesktopSafari
        || /Mac|iPhone|iPad|iPod/i.test(platform)
        || /Macintosh|Mac OS X/i.test(userAgent);
      this.supportsNativeHls = Boolean(
        this.video?.canPlayType('application/vnd.apple.mpegurl')
        || this.video?.canPlayType('application/x-mpegURL')
      );
      this.activeViewport = '';
      this.mediaPrepared = false;
      this.mediaReadyPromise = null;
      this.mediaWarmPromise = null;
      this.playPromise = null;
      this.isPrewarming = false;
      this.lastPageScrollAt = performance.now();
      this.prewarmTimer = 0;
      this.prewarmResolve = null;
      this.hasPlaybackStarted = false;
      this.readyFramePending = false;
      this.readyFrameCallbackId = 0;
      this.readyFrameRaf = 0;
      this.destroyed = false;
      this.mediaObserver = null;
      this.buttonCleanups = [];
      this.onPageScroll = () => {
        this.lastPageScrollAt = performance.now();
      };

      this.onViewportChange = () => {
        if (this.mediaPrepared) {
          this.syncVideoSources(true);
        } else {
          this.activeViewport = '';
        }
      };
      this.onVideoPlaying = () => {
        if (this.isPrewarming) {
          this.root.dataset.mediaState = 'warming';
          return;
        }
        this.hasPlaybackStarted = true;
        if (this.readyFramePending) return;
        this.readyFramePending = true;
        const expectedViewport = this.activeViewport;
        const revealDecodedFrame = () => {
          this.readyFrameCallbackId = 0;
          this.readyFrameRaf = 0;
          this.readyFramePending = false;
          if (this.destroyed || this.activeViewport !== expectedViewport) return;
          this.root.classList.add('is-media-ready');
          this.root.dataset.mediaState = 'playing';
        };
        if (typeof this.video.requestVideoFrameCallback === 'function') {
          this.readyFrameCallbackId = this.video.requestVideoFrameCallback(revealDecodedFrame);
        } else {
          this.readyFrameRaf = requestAnimationFrame(() => {
            this.readyFrameRaf = requestAnimationFrame(revealDecodedFrame);
          });
        }
      };
      this.onVideoError = () => {
        this.cancelReadyFrameWait();
        this.root.classList.remove('is-media-ready');
        this.root.dataset.mediaState = 'poster';
      };
      this.onVideoPause = () => this.cancelReadyFrameWait();

      this.bindVideo();
      this.bindFeatureDock();
      window.addEventListener('scroll', this.onPageScroll, { passive: true });
      this.root.dataset.heroExperienceVersion = VERSION;
    }

    cancelReadyFrameWait() {
      if (this.readyFrameCallbackId && typeof this.video?.cancelVideoFrameCallback === 'function') {
        this.video.cancelVideoFrameCallback(this.readyFrameCallbackId);
      }
      if (this.readyFrameRaf) cancelAnimationFrame(this.readyFrameRaf);
      this.readyFrameCallbackId = 0;
      this.readyFrameRaf = 0;
      this.readyFramePending = false;
    }

    bindVideo() {
      if (!this.video || !this.sources.length) return;

      this.video.addEventListener('playing', this.onVideoPlaying);
      this.video.addEventListener('pause', this.onVideoPause);
      this.video.addEventListener('error', this.onVideoError);
      this.mobileQuery.addEventListener?.('change', this.onViewportChange);
      this.root.dataset.mediaState = 'deferred';

      if (this.reducedMotion) {
        this.video.pause();
        this.video.removeAttribute('autoplay');
        this.root.dataset.mediaState = 'poster';
        return;
      }

      if ('IntersectionObserver' in window) {
        this.mediaObserver = new IntersectionObserver((entries) => {
          const entry = entries.find((candidate) => candidate.target === this.root);
          if (!entry) return;
          if (entry.isIntersecting) {
            this.prepareVideo();
            if (!this.isBurnHero && this.hasPlaybackStarted && this.video.paused) this.playVideo();
          } else if (this.mediaPrepared && !this.video.paused) {
            this.video.pause();
            this.root.dataset.mediaState = 'paused-offscreen';
          }
        // Keep the lower margin for early preparation, but stop decoding and
        // compositing once only the completed handoff sliver remains above.
        }, { rootMargin: '-96px 0px 45% 0px', threshold: 0 });
        this.mediaObserver.observe(this.root);
      } else {
        this.prepareVideo();
      }
    }

    preferredViewport() {
      const requested = this.mobileQuery.matches ? 'mobile' : 'desktop';
      const hasRequested = this.sources.some((source) => source.dataset.viewport === requested);
      if (hasRequested) return requested;
      if (this.sources.some((source) => source.dataset.viewport === 'all')) return 'all';
      return requested === 'mobile' ? 'desktop' : 'mobile';
    }

    syncVideoSources(shouldResume) {
      if (!this.video || !this.sources.length || this.destroyed) return;
      const viewport = this.preferredViewport();
      if (viewport === this.activeViewport) return;

      const wasPlaying = !this.video.paused;
      this.cancelReadyFrameWait();
      this.video.pause();
      this.root.classList.remove('is-media-ready');

      const activeSources = this.sources
        .filter((source) => source.dataset.viewport === viewport)
        .sort((sourceA, sourceB) => this.sourcePriority(sourceA) - this.sourcePriority(sourceB));
      const inactiveSources = this.sources.filter((source) => source.dataset.viewport !== viewport);
      [...activeSources, ...inactiveSources].forEach((source) => this.video.appendChild(source));

      this.sources.forEach((source) => {
        const isActive = source.dataset.viewport === viewport;
        if (isActive) {
          source.src = source.dataset.src;
        } else {
          source.removeAttribute('src');
        }
      });

      this.activeViewport = viewport;
      this.mediaReadyPromise = null;
      this.mediaWarmPromise = null;
      this.playPromise = null;
      this.video.dataset.activeViewport = viewport;
      this.video.dataset.preferredFormat = this.shouldPreferAdaptiveHls() ? 'hls' : 'mp4';
      this.video.load();
      this.root.dataset.mediaViewport = viewport;
      this.root.dataset.mediaState = 'loading';

      if (shouldResume) {
        if (wasPlaying || !this.isBurnHero) {
          this.mediaReadyPromise = this.waitUntilCanPlay();
          this.mediaReadyPromise.then((isReady) => {
            if (isReady) this.playVideo();
          });
        } else if (this.mediaPrepared) {
          this.prepareVideo();
        }
      }
    }

    sourcePriority(source) {
      const type = source.type || '';
      const isHls = /mpegurl/i.test(type);
      const isMp4 = type === 'video/mp4';
      const isPlayable = !type || this.video.canPlayType(type) !== '';
      if (!isPlayable) return 10;
      if (this.shouldPreferAdaptiveHls()) return isHls ? 0 : isMp4 ? 1 : 2;
      return isMp4 ? 0 : isHls ? 1 : 2;
    }

    shouldPreferAdaptiveHls() {
      // Looping HLS can expose a black seam on iOS. The burn hero owns its
      // playback timing, so prefer Shopify's MP4 rendition for a clean loop.
      if (this.isBurnHero) return false;
      return this.supportsNativeHls && (this.isApplePlatform || this.mobileQuery.matches);
    }

    prepareVideo() {
      if (!this.video || this.reducedMotion || this.destroyed) return Promise.resolve(false);

      if (!this.mediaPrepared) {
        this.mediaPrepared = true;
        this.video.preload = 'auto';
        this.syncVideoSources(false);
      }

      if (!this.mediaReadyPromise) {
        this.mediaReadyPromise = this.waitUntilCanPlay();
      }

      if (!this.mediaWarmPromise) {
        this.mediaWarmPromise = this.mediaReadyPromise.then(async (isReady) => {
          if (!isReady || this.destroyed) return false;

          if (this.isBurnHero && !this.isDesignMode) {
            const canPrewarm = await this.waitForScrollQuiet();
            if (canPrewarm) await this.prewarmFirstFrame();
          } else if (!this.isBurnHero) {
            this.playVideo();
          }

          return true;
        });
      }

      return this.mediaWarmPromise;
    }

    waitForScrollQuiet(quietWindow = 180, maxWait = 1100) {
      this.prewarmResolve?.();
      const startedAt = performance.now();
      return new Promise((resolve) => {
        const finish = (shouldWarm) => {
          window.clearTimeout(this.prewarmTimer);
          this.prewarmTimer = 0;
          this.prewarmResolve = null;
          resolve(shouldWarm);
        };
        const check = () => {
          if (this.destroyed) {
            finish(false);
            return;
          }
          if (performance.now() - this.lastPageScrollAt >= quietWindow) {
            finish(true);
            return;
          }
          if (performance.now() - startedAt >= maxWait) {
            finish(false);
            return;
          }
          this.prewarmTimer = window.setTimeout(check, 60);
        };
        this.prewarmResolve = () => finish(false);
        check();
      });
    }

    waitUntilCanPlay(timeout = 3600) {
      if (!this.video) return Promise.resolve(false);
      if (this.video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        this.root.dataset.mediaState = 'buffered';
        return Promise.resolve(true);
      }

      return new Promise((resolve) => {
        let settled = false;
        const finish = (isReady) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          this.video.removeEventListener('canplay', onCanPlay);
          this.video.removeEventListener('error', onError);
          if (isReady && !this.destroyed) this.root.dataset.mediaState = 'buffered';
          resolve(isReady);
        };
        const onCanPlay = () => finish(true);
        const onError = () => finish(false);
        const timer = window.setTimeout(() => finish(this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA), timeout);
        this.video.addEventListener('canplay', onCanPlay, { once: true });
        this.video.addEventListener('error', onError, { once: true });
      });
    }

    waitForDecodedFrame(timeout = 700) {
      if (!this.video || this.destroyed) return Promise.resolve(false);

      return new Promise((resolve) => {
        let settled = false;
        let frameCallbackId = null;
        let fallbackTimer = 0;
        const finish = (isReady) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          window.clearTimeout(fallbackTimer);
          if (frameCallbackId !== null && 'cancelVideoFrameCallback' in this.video) {
            this.video.cancelVideoFrameCallback(frameCallbackId);
          }
          resolve(isReady);
        };
        const timer = window.setTimeout(() => finish(false), timeout);

        if ('requestVideoFrameCallback' in this.video) {
          frameCallbackId = this.video.requestVideoFrameCallback(() => finish(true));
        } else {
          fallbackTimer = window.setTimeout(
            () => finish(this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA),
            96
          );
        }
      });
    }

    rewindPrewarmedVideo(timeout = 700) {
      if (!this.video || this.video.currentTime <= 0.04) return Promise.resolve();

      return new Promise((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          this.video.removeEventListener('seeked', finish);
          this.video.removeEventListener('error', finish);
          resolve();
        };
        const timer = window.setTimeout(finish, timeout);
        this.video.addEventListener('seeked', finish, { once: true });
        this.video.addEventListener('error', finish, { once: true });
        try {
          this.video.currentTime = 0;
        } catch (_error) {
          finish();
        }
      });
    }

    async prewarmFirstFrame() {
      if (
        !this.video
        || this.destroyed
        || !this.video.paused
        || (this.hasPlaybackStarted && this.root.classList.contains('is-media-ready'))
      ) return;

      this.isPrewarming = true;
      let didPrewarm = false;
      let acceptPrewarmPlayback = true;
      this.root.dataset.mediaState = 'warming';
      try {
        const playAttempt = Promise.resolve(this.video.play())
          .then(() => {
            if (!acceptPrewarmPlayback || this.destroyed) {
              this.video.pause();
              return false;
            }
            return true;
          })
          .catch(() => false);
        const didStart = await Promise.race([
          playAttempt,
          new Promise((resolve) => window.setTimeout(() => resolve(false), 1200))
        ]);
        if (didStart) didPrewarm = await this.waitForDecodedFrame();
      } catch (_error) {
        // A decoded poster frame is still available when autoplay prewarming is blocked.
      } finally {
        acceptPrewarmPlayback = false;
        this.video.pause();
        const hasDecodedFrame = didPrewarm
          || this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;
        // A delayed requestVideoFrameCallback can time out after playback has
        // already advanced. Always rewind so the real reveal starts at frame 0.
        await this.rewindPrewarmedVideo();
        this.isPrewarming = false;
        if (!this.destroyed) this.root.dataset.mediaState = hasDecodedFrame ? 'prewarmed' : 'buffered';
      }
    }

    playVideo() {
      if (!this.video || this.reducedMotion || this.destroyed) return Promise.resolve(false);
      if (!this.video.paused && !this.video.ended) return Promise.resolve(true);
      if (this.playPromise) return this.playPromise;

      const startPlayback = async () => {
        if (this.isPrewarming && this.mediaWarmPromise) await this.mediaWarmPromise;
        if (this.destroyed || this.reducedMotion) return false;
        if (!this.video.paused && !this.video.ended) return true;

        try {
          await this.video.play();
          if (this.destroyed) {
            this.video.pause();
            return false;
          }
          this.root.dataset.mediaState = 'playing';
          return true;
        } catch (_error) {
          this.root.dataset.mediaState = 'poster';
          return false;
        }
      };

      const playPromise = startPlayback().finally(() => {
        if (this.playPromise === playPromise) this.playPromise = null;
      });
      this.playPromise = playPromise;
      return playPromise;
    }

    bindFeatureDock() {
      if (!this.featureButtons.length) return;

      this.featureButtons.forEach((button, index) => {
        const onClick = () => this.selectFeature(index);
        const onPointerEnter = () => {
          if (this.hoverQuery.matches) this.selectFeature(index);
        };
        const onKeydown = (event) => {
          const keyMap = {
            ArrowRight: 1,
            ArrowDown: 1,
            ArrowLeft: -1,
            ArrowUp: -1
          };

          if (event.key === 'Home') {
            event.preventDefault();
            this.selectFeature(0, true);
            return;
          }
          if (event.key === 'End') {
            event.preventDefault();
            this.selectFeature(this.featureButtons.length - 1, true);
            return;
          }
          if (!keyMap[event.key]) return;
          event.preventDefault();
          const nextIndex = (index + keyMap[event.key] + this.featureButtons.length) % this.featureButtons.length;
          this.selectFeature(nextIndex, true);
        };

        button.addEventListener('click', onClick);
        button.addEventListener('pointerenter', onPointerEnter);
        button.addEventListener('keydown', onKeydown);
        this.buttonCleanups.push(() => {
          button.removeEventListener('click', onClick);
          button.removeEventListener('pointerenter', onPointerEnter);
          button.removeEventListener('keydown', onKeydown);
        });
      });

      const initialIndex = Math.max(0, this.featureButtons.findIndex((button) => button.closest('[data-moztech-feature-item]')?.classList.contains('is-active')));
      this.selectFeature(initialIndex);
    }

    selectFeature(activeIndex, shouldFocus = false) {
      this.featureButtons.forEach((button, index) => {
        const isActive = index === activeIndex;
        const item = button.closest('[data-moztech-feature-item]');
        const panel = button.querySelector('[data-moztech-feature-panel]');
        item?.classList.toggle('is-active', isActive);
        button.setAttribute('aria-expanded', String(isActive));
        panel?.setAttribute('aria-hidden', String(!isActive));
      });

      this.root.dataset.activeFeature = String(activeIndex + 1);
      if (shouldFocus) this.featureButtons[activeIndex]?.focus({ preventScroll: true });
    }

    destroy() {
      this.destroyed = true;
      this.cancelReadyFrameWait();
      this.prewarmResolve?.();
      window.clearTimeout(this.prewarmTimer);
      this.playPromise = null;
      this.mediaObserver?.disconnect();
      this.mobileQuery.removeEventListener?.('change', this.onViewportChange);
      window.removeEventListener('scroll', this.onPageScroll);
      this.video?.removeEventListener('playing', this.onVideoPlaying);
      this.video?.removeEventListener('pause', this.onVideoPause);
      this.video?.removeEventListener('timeupdate', this.onVideoPlaying);
      this.video?.removeEventListener('error', this.onVideoError);
      this.video?.pause();
      this.buttonCleanups.forEach((cleanup) => cleanup());
      this.buttonCleanups = [];
      delete this.root._moztechHeroExperience;
    }
  }

  const init = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-moztech-hero]')) roots.push(scope);
    scope.querySelectorAll?.('[data-moztech-hero]').forEach((root) => roots.push(root));

    roots.forEach((root) => {
      if (root._moztechHeroExperience) return;
      root._moztechHeroExperience = new MoztechHeroExperienceInstance(root);
    });
  };

  const destroy = (scope) => {
    const root = scope.matches?.('[data-moztech-hero]') ? scope : scope.querySelector?.('[data-moztech-hero]');
    root?._moztechHeroExperience?.destroy();
  };

  window.MoztechHeroExperience = { version: VERSION, init, destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
